import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, ArrowLeft, CheckCircle, Upload, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const steps = ['Personal Details', 'Professional Info', 'Documents', 'Review & Submit'];

export default function ApplyPage() {
  const { token } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [examCenters, setExamCenters] = useState([]);
  const [form, setForm] = useState({
    full_name: '', date_of_birth: '', gender: '', nationality: '',
    medical_reg_number: '', issuing_authority: '', years_of_practice: '',
    specialty: '', examination: '', route: '', examination_center_id: '', declaration: false,
    documents: { license: null, qualifications: null, experience: null, id: null, photo: null }
  });

  // Fetch approved examination centers
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const res = await axios.get(`${API}/approved-examination-centers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExamCenters(res.data);
      } catch (err) {
        console.error('Failed to fetch examination centers:', err);
      }
    };
    if (token) fetchCenters();
  }, [token]);

  const examinations = [
    { value: 'CBEAM', label: 'CBEAM - Competency-Based Examination in Aesthetic Medicine', category: 'Medical Doctors' },
    { value: 'CBELM', label: 'CBELM - Competency-Based Examination in Longevity Medicine', category: 'Medical Doctors' },
    { value: 'CBERM', label: 'CBERM - Competency-Based Examination in Regenerative Medicine', category: 'Medical Doctors' },
    { value: 'CBEAD', label: 'CBEAD - Competency-Based Examination in Aesthetic Dentistry', category: 'Dental' },
    { value: 'CBENA', label: 'CBENA - Competency-Based Examination in Aesthetic Nursing', category: 'Allied' },
    { value: 'CBEAA', label: 'CBEAA - Competency-Based Examination in Clinical Aesthetic Assistance', category: 'Allied' },
  ];

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // Upload a single file to the server
  const uploadFile = async (docKey, file) => {
    setUploading(prev => ({ ...prev, [docKey]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Store the uploaded file info
      updateForm('documents', { 
        ...form.documents, 
        [docKey]: { 
          name: file.name, 
          url: res.data.url, 
          file_id: res.data.file_id,
          stored_name: res.data.stored_name 
        } 
      });
      toast.success(`${file.name} uploaded successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
    } finally {
      setUploading(prev => ({ ...prev, [docKey]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!form.declaration) { toast.error('You must accept the declaration'); return; }
    setLoading(true);
    try {
      // Prepare documents with URLs instead of just names
      const documentsData = Object.fromEntries(
        Object.entries(form.documents).map(([k, v]) => [k, v ? { name: v.name, url: v.url, file_id: v.file_id } : null])
      );
      const response = await axios.post(`${API}/applications`, {
        ...form,
        years_of_practice: parseInt(form.years_of_practice) || 0,
        documents: documentsData
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (response.data && response.data.id) {
        setSubmitted(true);
        toast.success('Application submitted successfully! Check your email for confirmation.');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Submission error:', err);
      const errorMessage = err.response?.data?.detail || 
                          (Array.isArray(err.response?.data) ? err.response.data.map(e => e.msg).join(', ') : 'Submission failed. Please try again.');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="apply-success">
        <Card className="imcac-card max-w-lg w-full mx-4">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading mb-2">Application Submitted</h2>
            <p className="text-sm text-slate-600 mb-6">Your application has been received and is under review. You will be notified of the outcome within 10 business days.</p>
            <Button onClick={() => window.location.href = '/dashboard'} className="bg-[#1B2A4A] text-white rounded-sm" data-testid="go-dashboard-btn">Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="apply-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <div className="flex items-center justify-between">
            <div>
              <p className="imcac-overline mb-3">Examination Application</p>
              <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Apply for Examination</h1>
              <div className="h-1 w-20 bg-[#C5A572] mt-5" />
            </div>
            <Link to="/dashboard">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1B2A4A] rounded-sm" data-testid="back-to-dashboard-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container max-w-3xl">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-xs text-slate-500 mb-2">
              {steps.map((s, i) => (
                <span key={i} className={`${i <= step ? 'text-[#1B2A4A] font-semibold' : ''}`}>{s}</span>
              ))}
            </div>
            <Progress value={(step + 1) / steps.length * 100} className="h-2 bg-slate-200 [&>div]:bg-[#C5A572]" data-testid="apply-progress" />
          </div>

          <Card className="imcac-card">
            <CardContent className="p-6 md:p-8">
              {/* Step 0: Personal Details */}
              {step === 0 && (
                <div className="space-y-4" data-testid="step-personal">
                  <h2 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Personal Details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Full Name</Label>
                      <Input value={form.full_name} onChange={e => updateForm('full_name', e.target.value)} placeholder="Dr. Full Name" className="rounded-sm mt-1" data-testid="input-full-name" />
                    </div>
                    <div>
                      <Label className="text-sm">Date of Birth</Label>
                      <Input type="date" value={form.date_of_birth} onChange={e => updateForm('date_of_birth', e.target.value)} className="rounded-sm mt-1" data-testid="input-dob" />
                    </div>
                    <div>
                      <Label className="text-sm">Gender</Label>
                      <Select value={form.gender} onValueChange={v => updateForm('gender', v)}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="select-gender">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Nationality</Label>
                      <Input value={form.nationality} onChange={e => updateForm('nationality', e.target.value)} placeholder="e.g., British" className="rounded-sm mt-1" data-testid="input-nationality" />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 1: Professional Info */}
              {step === 1 && (
                <div className="space-y-4" data-testid="step-professional">
                  <h2 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Professional Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium text-slate-700">Select Examination *</Label>
                      <Select value={form.examination} onValueChange={v => updateForm('examination', v)}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="select-examination">
                          <SelectValue placeholder="Select the examination you wish to apply for" />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Medical Doctors</div>
                          {examinations.filter(e => e.category === 'Medical Doctors').map(exam => (
                            <SelectItem key={exam.value} value={exam.value}>{exam.label}</SelectItem>
                          ))}
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 mt-1">Dental</div>
                          {examinations.filter(e => e.category === 'Dental').map(exam => (
                            <SelectItem key={exam.value} value={exam.value}>{exam.label}</SelectItem>
                          ))}
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 mt-1">Allied (Nursing & Assistants)</div>
                          {examinations.filter(e => e.category === 'Allied').map(exam => (
                            <SelectItem key={exam.value} value={exam.value}>{exam.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Medical/Professional Registration Number</Label>
                      <Input value={form.medical_reg_number} onChange={e => updateForm('medical_reg_number', e.target.value)} placeholder="GMC-1234567" className="rounded-sm mt-1" data-testid="input-reg-number" />
                    </div>
                    <div>
                      <Label className="text-sm">Issuing Authority</Label>
                      <Input value={form.issuing_authority} onChange={e => updateForm('issuing_authority', e.target.value)} placeholder="e.g., GMC, Dental Council, Nursing Council" className="rounded-sm mt-1" data-testid="input-authority" />
                    </div>
                    <div>
                      <Label className="text-sm">Years of Practice</Label>
                      <Input type="number" value={form.years_of_practice} onChange={e => updateForm('years_of_practice', e.target.value)} placeholder="5" className="rounded-sm mt-1" data-testid="input-years" />
                    </div>
                    <div>
                      <Label className="text-sm">Current Specialty/Role</Label>
                      <Input value={form.specialty} onChange={e => updateForm('specialty', e.target.value)} placeholder="e.g., Aesthetic Medicine, Dental Practice" className="rounded-sm mt-1" data-testid="input-specialty" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm">Examination Route</Label>
                      <Select value={form.route} onValueChange={v => updateForm('route', v)}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="select-route">
                          <SelectValue placeholder="Select examination route" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A">Route A - Full Examination Pathway</SelectItem>
                          <SelectItem value="B">Route B - Experienced Practitioner Pathway</SelectItem>
                          <SelectItem value="C">Route C - Accredited Training Completion</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <MapPin className="h-4 w-4" /> Preferred Examination Center *
                      </Label>
                      <Select value={form.examination_center_id} onValueChange={v => updateForm('examination_center_id', v)}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="select-exam-center">
                          <SelectValue placeholder="Select your preferred examination center" />
                        </SelectTrigger>
                        <SelectContent>
                          {examCenters.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-slate-500">No examination centers available</div>
                          ) : (
                            examCenters.map(center => (
                              <SelectItem key={center.id} value={center.id}>
                                {center.name} {center.city && `- ${center.city}`} {center.country && `(${center.country})`}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">Select the examination center where you wish to appear for OSCE and practical examinations</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Documents */}
              {step === 2 && (
                <div className="space-y-4" data-testid="step-documents">
                  <h2 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Document Upload</h2>
                  <p className="text-sm text-slate-600 mb-4">Upload your documents (PDF, JPG, PNG, DOC - max 10MB each)</p>
                  {[
                    { key: 'license', label: 'Medical License (Certified Copy)' },
                    { key: 'qualifications', label: 'Qualification Certificates' },
                    { key: 'experience', label: 'Experience Proof / Employment Letters' },
                    { key: 'id', label: 'Government-Issued ID' },
                    { key: 'photo', label: 'Passport-Sized Photograph' },
                  ].map(doc => (
                    <div key={doc.key} className="flex items-center justify-between p-4 bg-[#F8FAFC] border border-slate-200 rounded-sm">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">{doc.label}</p>
                        {form.documents[doc.key] && (
                          <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" /> {form.documents[doc.key].name}
                          </p>
                        )}
                      </div>
                      <label className="cursor-pointer">
                        <input 
                          type="file" 
                          className="hidden" 
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          onChange={e => {
                            if (e.target.files[0]) {
                              uploadFile(doc.key, e.target.files[0]);
                            }
                          }} 
                          data-testid={`upload-${doc.key}`} 
                          disabled={uploading[doc.key]}
                        />
                        <div className={`flex items-center gap-2 px-4 py-2 border rounded-sm text-sm transition-colors ${
                          uploading[doc.key] 
                            ? 'bg-slate-100 border-slate-300 text-slate-400 cursor-wait' 
                            : form.documents[doc.key]
                              ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                              : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                        }`}>
                          {uploading[doc.key] ? (
                            <>
                              <div className="h-4 w-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" /> {form.documents[doc.key] ? 'Replace' : 'Upload'}
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {/* Step 3: Review */}
              {step === 3 && (
                <div className="space-y-4" data-testid="step-review">
                  <h2 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Review & Submit</h2>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {[
                      ['Name', form.full_name], ['Date of Birth', form.date_of_birth], ['Gender', form.gender],
                      ['Nationality', form.nationality], ['Registration', form.medical_reg_number], ['Authority', form.issuing_authority],
                      ['Experience', `${form.years_of_practice} years`], ['Specialty', form.specialty], ['Route', `Route ${form.route}`],
                    ].map(([label, value]) => (
                      <div key={label} className="p-3 bg-[#F8FAFC] rounded-sm">
                        <p className="text-xs text-slate-500">{label}</p>
                        <p className="font-medium text-[#1B2A4A]">{value || '-'}</p>
                      </div>
                    ))}
                  </div>
                  <Separator className="my-4" />
                  <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-sm">
                    <Checkbox
                      id="declaration"
                      checked={form.declaration}
                      onCheckedChange={v => updateForm('declaration', v)}
                      data-testid="declaration-checkbox"
                    />
                    <label htmlFor="declaration" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                      I confirm I hold valid medical registration and understand that IMCAC certification does not grant medical licensure. 
                      I declare that all information provided is accurate and complete.
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">Online payment integration will be available upon approval of your application.</p>
                </div>
              )}

              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="rounded-sm" data-testid="prev-step-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Previous
                </Button>
                {step < 3 ? (
                  <Button onClick={() => setStep(step + 1)} className="bg-[#1B2A4A] text-white rounded-sm" data-testid="next-step-btn">
                    Next <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={loading || !form.declaration} className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm" data-testid="submit-application-btn">
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
